import { t } from "i18next";
import { useState } from "react";

import { computationOptions } from "#app/computationOptions";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { Icon } from "#shared/icons/Icon";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Flex } from "#shared/view/components/Flex";
import { Paper } from "#shared/view/components/Paper";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { computeAssemblyPrice, formatPrice } from "shared";

export const DetailsPanelFooter = ({
  quoteRequestLineId,
  hasChildren,
}: {
  quoteRequestLineId: string;
  hasChildren: boolean;
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const analytics = useAnalytics();

  const { quoteRequestLine } = useQuoteRequestLine({ id: quoteRequestLineId });
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestLine?.quoteRequestId);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestLine?.quoteRequestId);

  const { assemblies } = useAssembliesContext();
  const currentAssembly = assemblies.find(
    (assembly) => assembly.quoteRequestLine.id === quoteRequestLine?.id,
  );

  const isAssemblyChild = !!quoteRequestLine?.assemblyParentId;
  const partQuantityInAssembly = quoteRequestLine?.part.quantityInAssembly ?? 1;

  const {
    assembly: {
      cost: assemblyCost,
      price: assemblyPrice,
      seriesCost: assemblySeriesCost,
      seriesPrice: assemblySeriesPrice,
    },
    parentQuoteRequestLine: {
      unitCost: parentQuoteRequestLineUnitCost,
      unitPrice: parentQuoteRequestLineUnitPrice,
      cost: parentQuoteRequestLineCost,
      price: parentQuoteRequestLinePrice,
    },
  } = quoteRequestLine
    ? computeAssemblyPrice(
        {
          children: currentAssembly?.children ?? [],
          quoteRequestLine,
          salesMarginRatio: 0,
          optimizedMachineTimes,
          optimizedMaterialCosts,
        },
        computationOptions,
      )
    : {
        assembly: {
          cost: 0,
          price: 0,
          seriesCost: 0,
          seriesPrice: 0,
        },
        parentQuoteRequestLine: {
          unitCost: 0,
          unitPrice: 0,
          cost: 0,
          price: 0,
        },
      };

  const toggleDetails = () => {
    const newState = !showDetails;
    setShowDetails(newState);
    analytics.capture(AnalyticsEvent.QuoteRequestLineCostDetailsClick, {
      expanded: newState,
      quote_request_line_id: quoteRequestLineId,
      has_children: hasChildren,
      is_assembly_child: isAssemblyChild,
    });
  };

  return (
    <Stack gap="xxs" align="end">
      <Flex gap="xs" justify="end" align="end" mb="xs">
        <Paper
          bd={"1px solid var(--mantine-color-violet-5)"}
          p="xxs"
          radius="xs"
          shadow="none"
          pos="relative"
          mih={75}
        >
          <Stack gap="0" justify="start">
            <Flex justify="space-between" align="center" gap="xs">
              <Text fw={700}>
                {`${t("quoteRequestLine.unitPrice")} : `}
                {formatPrice(hasChildren ? assemblyPrice : parentQuoteRequestLineUnitPrice)}
              </Text>
              <ActionIcon
                variant="subtle"
                size="xs"
                onClick={toggleDetails}
                aria-label={
                  showDetails ? t("quoteRequestLine.hideDetail") : t("quoteRequestLine.showDetail")
                }
              >
                {showDetails ? <Icon name="chevronUp" /> : <Icon name="chevronDown" />}
              </ActionIcon>
            </Flex>
            <Text fz="xs">
              {`${t("quoteRequestLine.unitCost")} : `}
              {formatPrice(hasChildren ? assemblyCost : parentQuoteRequestLineUnitCost)}
            </Text>
            <Text fz="xs">
              {`${t("quoteRequestLine.totalSeriesPrice")} : `}
              {formatPrice(assemblySeriesPrice)}
            </Text>
            {showDetails && (
              <Stack mt="xxs" gap="0">
                {isAssemblyChild && (
                  <>
                    <Text fz="xs" c="dimmed">
                      {`${t("quoteRequestLine.partPrice", {
                        count: partQuantityInAssembly,
                      })} : `}
                      {formatPrice(parentQuoteRequestLinePrice)}
                    </Text>
                    <Text fz="xs" c="dimmed">
                      {`${t("quoteRequestLine.partCost", {
                        count: partQuantityInAssembly,
                      })} : `}
                      {formatPrice(parentQuoteRequestLineCost)}
                    </Text>
                  </>
                )}
                <Text fz="xs" c="dimmed">
                  {`${t("quoteRequestLine.seriesCost")} : `}
                  {formatPrice(assemblySeriesCost)}
                </Text>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Flex>
    </Stack>
  );
};
